import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { useUnit } from 'effector-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { apartmentsApi } from '../api/apartments';
import { buildingCorpsApi } from '../api/buildingCorps';
import { devicesApi } from '../api/devices';
import { residentialComplexesApi } from '../api/residentialComplexes';
import { ticketsApi } from '../api/tickets';
import { mapRoleIdToLabel } from '../config/roles';
import { $canEditTickets } from '../store/auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'new', label: 'Новая' },
  { value: 'in_review', label: 'На проверке' },
  { value: 'assigned', label: 'Назначена' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'waiting_for_resident', label: 'Ждет жильца' },
  { value: 'waiting_for_contractor', label: 'Ждет исполнителя' },
  { value: 'resolved', label: 'Решена' },
  { value: 'closed', label: 'Закрыта' },
  { value: 'cancelled', label: 'Отменена' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'Все приоритеты' },
  { value: 'low', label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
];

const CATEGORY_OPTIONS = [
  { value: 'emergency', label: 'Авария' },
  { value: 'plumbing', label: 'Сантехника' },
  { value: 'electrical', label: 'Электрика' },
  { value: 'heating', label: 'Отопление' },
  { value: 'cleaning', label: 'Уборка' },
  { value: 'intercom', label: 'Домофон' },
  { value: 'elevator', label: 'Лифт' },
  { value: 'landscaping', label: 'Благоустройство' },
  { value: 'billing', label: 'Начисления / квитанции' },
  { value: 'other', label: 'Другое' },
];

const SOURCE_OPTIONS = [
  { value: 'EMAIL', label: 'Электронная почта' },
  { value: 'CALL', label: 'Звонок' },
  { value: 'TELEGRAM', label: 'Telegram' },
  { value: 'MAX', label: 'MAX' },
  { value: 'WEB', label: 'Веб-форма' },
  { value: 'MOBILE', label: 'Мобильное приложение' },
  { value: 'OTHER', label: 'Другое' },
];

const STATUS_META = {
  new: { label: 'Новая', bg: '#E5F0FF', color: '#2357A7' },
  in_review: { label: 'На проверке', bg: '#EDE9FE', color: '#5B3CC4' },
  assigned: { label: 'Назначена', bg: '#E8F5E9', color: '#2E7D32' },
  in_progress: { label: 'В работе', bg: '#FFF3D6', color: '#9A6700' },
  waiting_for_resident: { label: 'Ждет жильца', bg: '#FFE5E5', color: '#B34747' },
  waiting_for_contractor: { label: 'Ждет исполнителя', bg: '#FFECD9', color: '#AF5B1F' },
  resolved: { label: 'Решена', bg: '#DCFCE7', color: '#166534' },
  closed: { label: 'Закрыта', bg: '#E5E7EB', color: '#374151' },
  cancelled: { label: 'Отменена', bg: '#F3E8FF', color: '#7C3AED' },
};

const PRIORITY_META = {
  low: { label: 'Низкий', bg: '#EEF2FF', color: '#4F46E5' },
  normal: { label: 'Обычный', bg: '#E2F6EC', color: '#166534' },
  high: { label: 'Высокий', bg: '#FFF1C2', color: '#9A6700' },
  urgent: { label: 'Срочный', bg: '#FFE1E1', color: '#B42318' },
};

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const dropdownFieldSx = {
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    borderRadius: 1.5,
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
    '& fieldset': {
      borderColor: '#C9D3E0',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: '#98A2B3',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2E73B8',
      borderWidth: 1,
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(46, 115, 184, 0.08)',
    },
    '&.Mui-disabled': {
      background: '#F8FAFC',
    },
    '&.Mui-disabled fieldset': {
      borderColor: '#D0D5DD',
    },
  },
  '& .MuiAutocomplete-inputRoot.Mui-focused': {
    boxShadow: '0 0 0 3px rgba(46, 115, 184, 0.08)',
  },
  '& .MuiAutocomplete-inputRoot.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2E73B8',
    borderWidth: '1px',
  },
};

const autocompleteSlotProps = {
  paper: {
    sx: {
      mt: 0.5,
      border: '1px solid #C9D3E0',
      borderRadius: 1.75,
      boxShadow: '0 18px 38px rgba(16, 24, 40, 0.12)',
      overflow: 'hidden',
    },
  },
  listbox: {
    sx: {
      py: 0.5,
      '& .MuiAutocomplete-option': {
        minHeight: 36,
        fontSize: 13,
        borderRadius: 1,
        mx: 0.5,
        my: 0.25,
      },
      '& .MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: '#EEF4FF',
      },
      '& .MuiAutocomplete-option.Mui-focused': {
        backgroundColor: '#F5F8FC',
      },
    },
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      border: '1px solid #C9D3E0',
      borderRadius: 1.75,
      boxShadow: '0 18px 38px rgba(16, 24, 40, 0.12)',
      overflow: 'hidden',
      '& .MuiList-root.MuiMenu-list': {
        p: 0.5,
        border: '1px solid transparent',
      },
      '& .MuiMenuItem-root': {
        minHeight: 36,
        fontSize: 13,
        borderRadius: 1,
        my: 0.25,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: '#EEF4FF',
      },
      '& .MuiMenuItem-root.Mui-focusVisible, & .MuiMenuItem-root:hover': {
        backgroundColor: '#F5F8FC',
      },
    },
  },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatFileSize = (value) => {
  const size = Number(value || 0);
  if (!size) return 'Файл';
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
};

const statusChipProps = (status) =>
  STATUS_META[status] || { label: status || '—', bg: '#EEF2F7', color: '#334155' };

const priorityChipProps = (priority) =>
  PRIORITY_META[priority] || { label: priority || '—', bg: '#EEF2F7', color: '#334155' };

const buildApartmentLabel = (apartment) => {
  if (!apartment) return 'Квартира не найдена';
  return `Кв. ${apartment.number}${apartment.suffix || ''}`;
};

const getCategoryLabel = (category) => {
  const normalized = String(category || 'other').trim().toLowerCase();
  if (normalized === 'general') return 'Другое';
  return CATEGORY_OPTIONS.find((option) => option.value === normalized)?.label || 'Другое';
};

const getSourceLabel = (source) => {
  const normalized = String(source || 'OTHER').trim().toUpperCase();
  return SOURCE_OPTIONS.find((option) => option.value === normalized)?.label || normalized;
};

const toDatetimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const formatDeadlineCaption = (value) => {
  if (!value) return 'Срок не назначен';
  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return 'Срок не назначен';
  const diffMs = due.getTime() - Date.now();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `Просрочено на ${Math.abs(diffDays)} дн.`;
  if (diffDays === 0) return 'Срок сегодня';
  return `Осталось ${diffDays} дн.`;
};

const getAssigneeLabel = (assignee) => {
  if (!assignee) return 'Не назначен';
  if (assignee.full_name) {
    return `${assignee.full_name} — ${mapRoleIdToLabel(assignee.role)}`;
  }
  return assignee.email || 'Не назначен';
};

function DetailInfoRow({ label, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        py: 1.15,
        borderBottom: '1px solid #F2F4F7',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          width: 136,
          flex: '0 0 auto',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: '#98A2B3',
        }}
      >
        {label}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
}

export default function TicketsPage() {
  const canEditTickets = useUnit($canEditTickets);
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const isDetailPage = Boolean(ticketId);
  const requestedApartmentId = searchParams.get('apartment_id') || '';
  const createRequested = searchParams.get('create') === '1';
  const handledCreateRequest = useRef('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: '', priority: '' });
  const [activeStatusCard, setActiveStatusCard] = useState('');

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [apartmentDevices, setApartmentDevices] = useState([]);
  const [apartmentDevicesLoading, setApartmentDevicesLoading] = useState(false);
  const [apartmentMap, setApartmentMap] = useState({});
  const [apartmentsByCorp, setApartmentsByCorp] = useState({});
  const [corpsByBuilding, setCorpsByBuilding] = useState({});
  const [complexes, setComplexes] = useState([]);
  const [complexesLoading, setComplexesLoading] = useState(false);

  const [draft, setDraft] = useState({ status: '', priority: '', assignee_user_id: '', due_at: '' });
  const [saving, setSaving] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: 'other',
    priority: 'normal',
    source: 'CALL',
    dueAt: '',
    assigneeUserId: '',
    complexId: '',
    buildingId: '',
    corpsId: '',
    apartmentId: '',
  });

  const resetCreateForm = useCallback(() => {
    setCreateForm({
      title: '',
      description: '',
      category: 'other',
      priority: 'normal',
      source: 'CALL',
      dueAt: '',
      assigneeUserId: '',
      complexId: '',
      buildingId: '',
      corpsId: '',
      apartmentId: '',
    });
  }, []);

  const ensureApartmentsLoaded = useCallback(async (apartmentIds) => {
    const missingIds = [...new Set((apartmentIds || []).filter(Boolean))].filter((id) => !apartmentMap[id]);
    if (!missingIds.length) return;

    const results = await Promise.allSettled(missingIds.map((id) => apartmentsApi.getById(id)));
    setApartmentMap((current) => {
      const next = { ...current };
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value?.id) {
          next[missingIds[index]] = result.value;
        }
      });
      return next;
    });
  }, [apartmentMap]);

  const loadComplexes = useCallback(async () => {
    setComplexesLoading(true);
    try {
      const data = await residentialComplexesApi.getResidentialComplexes(0, 100);
      setComplexes(Array.isArray(data) ? data : []);
    } catch (err) {
      setComplexes([]);
      setError((current) => current || err?.response?.data?.detail || err?.message || 'Не удалось загрузить объекты');
    } finally {
      setComplexesLoading(false);
    }
  }, []);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    try {
      const data = await ticketsApi.listAssignees();
      setAssignees(Array.isArray(data) ? data : []);
    } catch (err) {
      setAssignees([]);
      setError((current) => current || err?.response?.data?.detail || err?.message || 'Не удалось загрузить исполнителей');
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  const loadApartmentDevices = useCallback(async (apartmentId) => {
    if (!apartmentId) {
      setApartmentDevices([]);
      return;
    }
    setApartmentDevicesLoading(true);
    try {
      const data = await devicesApi.getByApartment(apartmentId);
      setApartmentDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      setApartmentDevices([]);
      setError((current) => current || err?.response?.data?.detail || err?.message || 'Не удалось загрузить приборы квартиры');
    } finally {
      setApartmentDevicesLoading(false);
    }
  }, []);

  const ensureCorpsLoaded = useCallback(async (buildingId) => {
    if (!buildingId || corpsByBuilding[buildingId]) return;
    try {
      const list = await buildingCorpsApi.getByBuilding(buildingId);
      setCorpsByBuilding((current) => ({ ...current, [buildingId]: Array.isArray(list) ? list : [] }));
    } catch (err) {
      setError((current) => current || err?.response?.data?.detail || err?.message || 'Не удалось загрузить корпуса');
    }
  }, [corpsByBuilding]);

  const loadApartmentsByCorpForCreate = useCallback(async (corpsId) => {
    if (!corpsId || apartmentsByCorp[corpsId]) return;
    try {
      const list = await apartmentsApi.getByCorp(corpsId);
      setApartmentsByCorp((current) => ({ ...current, [corpsId]: Array.isArray(list) ? list : [] }));
    } catch (err) {
      setError((current) => current || err?.response?.data?.detail || err?.message || 'Не удалось загрузить квартиры');
    }
  }, [apartmentsByCorp]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ticketsApi.list({
        search: filters.search.trim() || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        limit: 120,
        offset: 0,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setTickets(items);
      setTotal(Number(data?.total || 0));
      await ensureApartmentsLoaded(items.map((ticket) => ticket.apartment_id));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить список заявок');
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [ensureApartmentsLoaded, filters]);

  const loadTicketDetails = useCallback(async (id) => {
    if (!id) {
      setSelectedTicket(null);
      setComments([]);
      setHistory([]);
      setAttachments([]);
      return;
    }

    setDetailLoading(true);
    setError('');
    try {
      const [ticket, nextComments, nextHistory, nextAttachments] = await Promise.all([
        ticketsApi.get(id),
        ticketsApi.listComments(id),
        ticketsApi.listHistory(id),
        ticketsApi.listAttachments(id),
      ]);
      setSelectedTicket(ticket);
      setDraft({
        status: ticket?.status || '',
        priority: ticket?.priority || '',
        assignee_user_id: ticket?.assignee_user_id || '',
        due_at: toDatetimeLocalValue(ticket?.due_at),
      });
      setComments(Array.isArray(nextComments) ? nextComments : []);
      setHistory(Array.isArray(nextHistory) ? nextHistory : []);
      setAttachments(Array.isArray(nextAttachments) ? nextAttachments : []);
      await ensureApartmentsLoaded([ticket?.apartment_id]);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить карточку заявки');
    } finally {
      setDetailLoading(false);
    }
  }, [ensureApartmentsLoaded]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    loadComplexes();
  }, [loadComplexes]);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  useEffect(() => {
    if (
      isDetailPage ||
      !canEditTickets ||
      !createRequested ||
      !requestedApartmentId ||
      !complexes.length ||
      handledCreateRequest.current === requestedApartmentId
    ) {
      return undefined;
    }

    let active = true;
    handledCreateRequest.current = requestedApartmentId;
    const prepareApartmentTicket = async () => {
      try {
        const overview = await apartmentsApi.getOverview(requestedApartmentId);
        const buildingId = String(overview.building_id || '');
        const corpsId = String(overview.corp_id || overview.apartment?.corp_id || '');
        const complex = complexes.find((item) => (
          Array.isArray(item.buildings) && item.buildings.some(
            (building) => String(building.id) === buildingId
          )
        ));
        if (!buildingId || !corpsId || !complex?.id) {
          throw new Error('Не удалось определить объект квартиры');
        }

        const [corps, apartments] = await Promise.all([
          buildingCorpsApi.getByBuilding(buildingId),
          apartmentsApi.getByCorp(corpsId),
        ]);
        if (!active) return;
        setCorpsByBuilding((current) => ({
          ...current,
          [buildingId]: Array.isArray(corps) ? corps : [],
        }));
        setApartmentsByCorp((current) => ({
          ...current,
          [corpsId]: Array.isArray(apartments) ? apartments : [],
        }));
        setApartmentMap((current) => ({
          ...current,
          [requestedApartmentId]: overview.apartment,
        }));
        setCreateForm((current) => ({
          ...current,
          complexId: String(complex.id),
          buildingId,
          corpsId,
          apartmentId: requestedApartmentId,
        }));
        setCreateOpen(true);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.detail || err?.message || 'Не удалось открыть форму заявки');
      }
    };
    prepareApartmentTicket();
    return () => {
      active = false;
    };
  }, [canEditTickets, complexes, createRequested, isDetailPage, requestedApartmentId]);

  useEffect(() => {
    if (!isDetailPage) {
      setSelectedTicket(null);
      setComments([]);
      setHistory([]);
      setAttachments([]);
      setApartmentDevices([]);
      return;
    }
    if (ticketId) {
      loadTicketDetails(ticketId);
    }
  }, [isDetailPage, loadTicketDetails, ticketId]);

  useEffect(() => {
    if (!isDetailPage || !selectedTicket?.apartment_id) {
      setApartmentDevices([]);
      return;
    }
    loadApartmentDevices(selectedTicket.apartment_id);
  }, [isDetailPage, loadApartmentDevices, selectedTicket?.apartment_id]);

  const selectedComplex = useMemo(
    () => complexes.find((item) => String(item.id) === String(createForm.complexId)) || null,
    [complexes, createForm.complexId]
  );

  const buildingOptions = useMemo(
    () => (Array.isArray(selectedComplex?.buildings) ? selectedComplex.buildings : []),
    [selectedComplex]
  );

  const corpsOptions = useMemo(
    () => corpsByBuilding[createForm.buildingId] || [],
    [corpsByBuilding, createForm.buildingId]
  );

  const apartmentOptions = useMemo(
    () => (createForm.corpsId ? apartmentsByCorp[createForm.corpsId] || [] : []),
    [apartmentsByCorp, createForm.corpsId]
  );

  const selectedBuilding = useMemo(
    () => buildingOptions.find((item) => String(item.id) === String(createForm.buildingId)) || null,
    [buildingOptions, createForm.buildingId]
  );

  const selectedCorps = useMemo(
    () => corpsOptions.find((item) => String(item.id) === String(createForm.corpsId)) || null,
    [corpsOptions, createForm.corpsId]
  );

  const selectedApartment = useMemo(
    () => apartmentOptions.find((item) => String(item.id) === String(createForm.apartmentId)) || null,
    [apartmentOptions, createForm.apartmentId]
  );

  const selectedCreateAssignee = useMemo(
    () => assignees.find((item) => String(item.id) === String(createForm.assigneeUserId)) || null,
    [assignees, createForm.assigneeUserId]
  );

  const selectedDraftAssignee = useMemo(
    () => assignees.find((item) => String(item.id) === String(draft.assignee_user_id)) || null,
    [assignees, draft.assignee_user_id]
  );

  useEffect(() => {
    if (!createOpen || createForm.complexId || complexes.length !== 1) return;
    setCreateForm((current) => ({ ...current, complexId: String(complexes[0].id) }));
  }, [complexes, createForm.complexId, createOpen]);

  useEffect(() => {
    if (!createOpen || !createForm.complexId || createForm.buildingId || buildingOptions.length !== 1) return;
    setCreateForm((current) => ({ ...current, buildingId: String(buildingOptions[0].id) }));
  }, [buildingOptions, createForm.buildingId, createForm.complexId, createOpen]);

  useEffect(() => {
    if (!createOpen || !createForm.buildingId) return;
    ensureCorpsLoaded(createForm.buildingId);
  }, [createForm.buildingId, createOpen, ensureCorpsLoaded]);

  useEffect(() => {
    if (!createOpen || !createForm.buildingId || createForm.corpsId || corpsOptions.length !== 1) return;
    setCreateForm((current) => ({ ...current, corpsId: String(corpsOptions[0].id) }));
  }, [corpsOptions, createForm.buildingId, createForm.corpsId, createOpen]);

  useEffect(() => {
    if (!createOpen || !createForm.corpsId) return;
    loadApartmentsByCorpForCreate(createForm.corpsId);
  }, [createForm.corpsId, createOpen, loadApartmentsByCorpForCreate]);

  useEffect(() => {
    if (!createOpen || !createForm.corpsId || createForm.apartmentId || apartmentOptions.length !== 1) return;
    setCreateForm((current) => ({ ...current, apartmentId: String(apartmentOptions[0].id) }));
  }, [apartmentOptions, createForm.apartmentId, createForm.corpsId, createOpen]);

  const handleCreateFormChange = (field, value) => {
    setCreateForm((current) => {
      if (field === 'complexId') {
        return { ...current, complexId: value, buildingId: '', corpsId: '', apartmentId: '' };
      }
      if (field === 'buildingId') {
        return { ...current, buildingId: value, corpsId: '', apartmentId: '' };
      }
      if (field === 'corpsId') {
        return { ...current, corpsId: value, apartmentId: '' };
      }
      return { ...current, [field]: value };
    });
  };

  const handleCreateTicket = async () => {
    if (!createForm.title.trim() || !createForm.apartmentId) {
      setError('Для создания заявки заполните тему и выберите квартиру.');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const created = await ticketsApi.create({
        title: createForm.title.trim(),
        description: createForm.description.trim() || null,
        category: createForm.category.trim() || 'other',
        priority: createForm.priority,
        status: 'new',
        source: createForm.source || 'OTHER',
        apartment_id: createForm.apartmentId,
        assignee_user_id: createForm.assigneeUserId || null,
        due_at: createForm.dueAt ? new Date(createForm.dueAt).toISOString() : null,
      });
      resetCreateForm();
      setCreateOpen(false);
      await ensureApartmentsLoaded([created.apartment_id]);
      await loadTickets();
      window.dispatchEvent(new Event('tickets-changed'));
      navigate(`/tickets/${created.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось создать заявку');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveTicket = async () => {
    if (!selectedTicket?.id) return;
    setSaving(true);
    setError('');
    try {
      await ticketsApi.update(selectedTicket.id, {
        status: draft.status,
        priority: draft.priority,
        assignee_user_id: draft.assignee_user_id || null,
        due_at: draft.due_at ? new Date(draft.due_at).toISOString() : null,
      });
      await loadTickets();
      await loadTicketDetails(selectedTicket.id);
      window.dispatchEvent(new Event('tickets-changed'));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось сохранить изменения');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedTicket?.id || !commentBody.trim()) return;
    setCommentSubmitting(true);
    setError('');
    try {
      await ticketsApi.createComment(selectedTicket.id, {
        body: commentBody.trim(),
        is_internal: commentInternal,
      });
      setCommentBody('');
      setCommentInternal(false);
      const nextComments = await ticketsApi.listComments(selectedTicket.id);
      setComments(Array.isArray(nextComments) ? nextComments : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось добавить комментарий');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    if (!selectedTicket?.id || !attachmentId) return;
    try {
      const data = await ticketsApi.getAttachmentDownloadUrl(selectedTicket.id, attachmentId);
      if (data?.download_url) {
        window.open(data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось открыть вложение');
    }
  };

  const uploadFileToPresignedUrl = useCallback((uploadUrl, file) => (
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      if (file.type) {
        xhr.setRequestHeader('Content-Type', file.type);
      }
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        setAttachmentUploadProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        reject(new Error('Не удалось загрузить файл в хранилище'));
      };
      xhr.onerror = () => reject(new Error('Не удалось загрузить файл в хранилище'));
      xhr.send(file);
    })
  ), []);

  const handleAttachmentFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!selectedTicket?.id || !file) return;

    setAttachmentUploading(true);
    setAttachmentUploadProgress(0);
    setError('');
    try {
      const presign = await ticketsApi.createAttachmentPresign(selectedTicket.id, {
        file_name: file.name,
        content_type: file.type || null,
      });
      await uploadFileToPresignedUrl(presign.upload_url, file);
      await ticketsApi.createAttachment(selectedTicket.id, {
        storage_bucket: presign.storage_bucket,
        storage_key: presign.storage_key,
        file_name: file.name,
        content_type: file.type || null,
        size_bytes: file.size,
      });
      const nextAttachments = await ticketsApi.listAttachments(selectedTicket.id);
      setAttachments(Array.isArray(nextAttachments) ? nextAttachments : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Не удалось загрузить вложение');
    } finally {
      setAttachmentUploading(false);
      setAttachmentUploadProgress(0);
    }
  };

  const selectedApartmentData = selectedTicket ? apartmentMap[selectedTicket.apartment_id] : null;
  const apartmentDeviceReadings = apartmentDevices.filter(
    (device) => device.last_data || device.last_data_time || device.last_seen_at
  );

  const overdueCount = tickets.filter((ticket) => {
    if (!ticket.due_at || ['resolved', 'closed', 'cancelled'].includes(ticket.status)) return false;
    return new Date(ticket.due_at).getTime() < Date.now();
  }).length;

  const statusCards = [
    {
      key: 'new',
      label: 'Новые',
      value: tickets.filter((ticket) => ticket.status === 'new').length,
      tone: { bg: '#E5F0FF', color: '#2357A7', border: 'rgba(35, 87, 167, 0.22)' },
    },
    {
      key: 'in_progress',
      label: 'В работе',
      value: tickets.filter((ticket) => ['assigned', 'in_progress'].includes(ticket.status)).length,
      tone: { bg: '#FFF3D6', color: '#9A6700', border: 'rgba(154, 103, 0, 0.2)' },
    },
    {
      key: 'resolved',
      label: 'Выполнены',
      value: tickets.filter((ticket) => ['resolved', 'closed'].includes(ticket.status)).length,
      tone: { bg: '#E7F8EE', color: '#13795B', border: 'rgba(19, 121, 91, 0.2)' },
    },
    {
      key: 'overdue',
      label: 'Просрочены',
      value: overdueCount,
      tone: { bg: '#F6EAFE', color: '#8C3EC8', border: 'rgba(140, 62, 200, 0.2)' },
    },
    {
      key: 'cancelled',
      label: 'Отклонены',
      value: tickets.filter((ticket) => ticket.status === 'cancelled').length,
      tone: { bg: '#FFE8EA', color: '#C0304D', border: 'rgba(192, 48, 77, 0.18)' },
    },
  ];

  const filteredTickets = useMemo(() => {
    let nextTickets = tickets;

    if (filters.status) {
      nextTickets = nextTickets.filter((ticket) => ticket.status === filters.status);
    }

    if (filters.priority) {
      nextTickets = nextTickets.filter((ticket) => ticket.priority === filters.priority);
    }

    if (!activeStatusCard) {
      return nextTickets;
    }

    if (activeStatusCard === 'new') {
      return nextTickets.filter((ticket) => ticket.status === 'new');
    }

    if (activeStatusCard === 'in_progress') {
      return nextTickets.filter((ticket) => ['assigned', 'in_progress', 'waiting_for_contractor', 'waiting_for_resident'].includes(ticket.status));
    }

    if (activeStatusCard === 'resolved') {
      return nextTickets.filter((ticket) => ['resolved', 'closed'].includes(ticket.status));
    }

    if (activeStatusCard === 'overdue') {
      return nextTickets.filter((ticket) => {
        if (!ticket.due_at || ['resolved', 'closed', 'cancelled'].includes(ticket.status)) return false;
        return new Date(ticket.due_at).getTime() < Date.now();
      });
    }

    if (activeStatusCard === 'cancelled') {
      return nextTickets.filter((ticket) => ticket.status === 'cancelled');
    }

    return nextTickets;
  }, [activeStatusCard, filters.priority, filters.status, tickets]);

  if (isDetailPage) {
    const currentStatus = statusChipProps(draft.status || selectedTicket?.status);
    const currentPriority = priorityChipProps(draft.priority || selectedTicket?.priority);
    const currentDueAt = draft.due_at || selectedTicket?.due_at || '';

    return (
      <Box
        className="home-container page-animate"
        sx={{
          px: { xs: 0, md: 1.5 },
          py: { xs: 0, md: 1.5 },
          maxWidth: 1520,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Stack spacing={0}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {detailLoading ? (
            <Paper sx={{ ...surfaceSx, minHeight: 720, borderRadius: { xs: 0, md: 4 } }}>
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 720 }}>
                <CircularProgress />
              </Stack>
            </Paper>
          ) : !selectedTicket ? (
            <Paper sx={{ ...surfaceSx, minHeight: 480, borderRadius: { xs: 0, md: 4 } }}>
              <Stack spacing={1.25} alignItems="center" justifyContent="center" sx={{ minHeight: 480, px: 3 }}>
                <ChatBubbleOutlineRoundedIcon color="disabled" />
                <Typography sx={{ fontWeight: 700 }}>Заявка не найдена</Typography>
                <Button
                  variant="text"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate('/tickets')}
                  sx={{ textTransform: 'none' }}
                >
                  Вернуться в журнал
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Paper sx={{ ...surfaceSx, overflow: 'hidden', borderRadius: { xs: 0, md: 4 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.4,
                  px: { xs: 1.75, md: 2.5 },
                  py: 1.4,
                  background: '#fff',
                  borderBottom: '1px solid #E4E7EC',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="text"
                  startIcon={<ArrowBackRoundedIcon />}
                  onClick={() => navigate('/tickets')}
                  sx={{ px: 0, minWidth: 'auto', minHeight: 28, textTransform: 'none', color: '#667085', fontSize: 12 }}
                >
                  Журнал
                </Button>
                <Typography sx={{ color: '#D0D5DD' }}>/</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#2E73B8', fontFamily: 'monospace' }}>
                  {selectedTicket.number}
                </Typography>
                <Typography sx={{ minWidth: 0, flex: 1, fontSize: 13, fontWeight: 500, color: '#101828' }} noWrap>
                  {selectedTicket.title}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={currentStatus.label}
                    sx={{ height: 22, borderRadius: 1, background: currentStatus.bg, color: currentStatus.color, fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
                  />
                  <Chip
                    size="small"
                    label={currentPriority.label}
                    sx={{ height: 22, borderRadius: 1, background: currentPriority.bg, color: currentPriority.color, fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
                  />
                  <Chip
                    size="small"
                    label={`История: ${history.length}`}
                    sx={{ height: 22, borderRadius: 1, background: '#F2F4F7', color: '#475467', fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSaveTicket}
                    disabled={saving || !canEditTickets}
                    sx={{
                      minHeight: 28,
                      px: 1.5,
                      borderRadius: 1.25,
                      textTransform: 'none',
                      boxShadow: 'none',
                      fontSize: 12,
                      background: '#2E73B8',
                      '&:hover': { background: '#245B92' },
                    }}
                  >
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  minHeight: { xs: 'auto', md: 760 },
                  flexDirection: { xs: 'column', md: 'row' },
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    background: '#fff',
                    borderRight: { xs: 'none', md: '1px solid #E4E7EC' },
                  }}
                >
                  <Box sx={{ borderBottom: '1px solid #E4E7EC' }}>
                    <Box
                      sx={{
                        px: { xs: 1.75, md: 2.5 },
                        py: 1.1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        borderBottom: '1px solid #F2F4F7',
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' }}>
                        Информация по заявке
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#98A2B3', fontFamily: 'monospace' }}>
                        Создана {formatDateTime(selectedTicket.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ px: { xs: 1.75, md: 2.5 }, py: 1.05 }}>
                      <DetailInfoRow label="Статус">
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            select
                            size="small"
                            disabled={!canEditTickets}
                            value={draft.status}
                            onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                            SelectProps={{ MenuProps: selectMenuProps }}
                            sx={{ ...dropdownFieldSx, minWidth: { xs: '100%', sm: 260 } }}
                          >
                            {STATUS_OPTIONS.filter((option) => option.value).map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </DetailInfoRow>

                      <DetailInfoRow label="Квартира">
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#101828' }}>
                          {selectedApartmentData ? buildApartmentLabel(selectedApartmentData) : '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#667085', mt: 0.1 }}>
                          {selectedApartmentData?.address || '—'}
                        </Typography>
                      </DetailInfoRow>

                      <DetailInfoRow label="Заявитель">
                        <Typography sx={{ fontSize: 12.5, color: '#101828' }}>
                          {selectedApartmentData?.owner_name || 'Не указан'}
                        </Typography>
                      </DetailInfoRow>

                      <DetailInfoRow label="Телефон">
                        <Typography sx={{ fontSize: 12.5, color: '#2E73B8', fontFamily: 'monospace' }}>
                          —
                        </Typography>
                      </DetailInfoRow>

                      <DetailInfoRow label="Источник">
                        <Typography sx={{ fontSize: 12.5, color: '#475467', fontFamily: 'monospace' }}>
                          {getSourceLabel(selectedTicket.source)}
                        </Typography>
                      </DetailInfoRow>

                      <DetailInfoRow label="Категория">
                        <TextField
                          select
                          size="small"
                          value={selectedTicket.category || 'other'}
                          disabled
                          SelectProps={{ MenuProps: selectMenuProps }}
                          sx={{ ...dropdownFieldSx, minWidth: { xs: '100%', sm: 260 } }}
                        >
                          {CATEGORY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </DetailInfoRow>

                      <DetailInfoRow label="Исполнитель">
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Autocomplete
                            size="small"
                            disabled={!canEditTickets}
                            options={assignees}
                            value={selectedDraftAssignee}
                            slotProps={autocompleteSlotProps}
                            noOptionsText="Нет доступных исполнителей"
                            loading={assigneesLoading}
                            onChange={(_, nextValue) =>
                              setDraft((current) => ({
                                ...current,
                                assignee_user_id: nextValue ? String(nextValue.id) : '',
                              }))
                            }
                            getOptionLabel={(option) => getAssigneeLabel(option)}
                            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                            renderInput={(params) => <TextField {...params} placeholder="Назначить исполнителя" />}
                            sx={{ ...dropdownFieldSx, width: { xs: '100%', sm: 320 } }}
                          />
                        </Stack>
                      </DetailInfoRow>

                      <DetailInfoRow label="Срок">
                        <Stack spacing={0.65}>
                          <TextField
                            size="small"
                            type="datetime-local"
                            disabled={!canEditTickets}
                            value={draft.due_at}
                            onChange={(event) => setDraft((current) => ({ ...current, due_at: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: { xs: '100%', sm: 260 } }}
                          />
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: currentDueAt && formatDeadlineCaption(currentDueAt).startsWith('Просрочено') ? '#B42318' : '#101828',
                              fontFamily: 'monospace',
                            }}
                          >
                            {currentDueAt ? formatDeadlineCaption(currentDueAt) : 'Срок не назначен'}
                          </Typography>
                        </Stack>
                      </DetailInfoRow>

                      <DetailInfoRow label="Приоритет">
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            select
                            size="small"
                            disabled={!canEditTickets}
                            value={draft.priority}
                            onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))}
                            SelectProps={{ MenuProps: selectMenuProps }}
                            sx={{ ...dropdownFieldSx, minWidth: { xs: '100%', sm: 260 } }}
                          >
                            {PRIORITY_OPTIONS.filter((option) => option.value).map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </DetailInfoRow>

                      <DetailInfoRow label="Описание">
                        <Typography sx={{ fontSize: 12.5, color: '#344054', lineHeight: 1.55 }}>
                          {selectedTicket.description?.trim() || 'Описание пока не заполнено'}
                        </Typography>
                      </DetailInfoRow>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        px: { xs: 1.75, md: 2.5 },
                        py: 1.1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        borderBottom: '1px solid #E4E7EC',
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' }}>
                        Комментарии · {comments.length} шт.
                      </Typography>
                    </Box>

                    <Stack spacing={1.5} sx={{ px: { xs: 1.75, md: 2.5 }, py: 1.5 }}>
                      <Box
                        sx={{
                          maxHeight: { xs: 320, md: 420 },
                          overflowY: 'auto',
                          pr: 0.5,
                        }}
                      >
                        <Stack spacing={1.5}>
                      {comments.length ? comments.map((comment) => (
                        <Stack
                          key={comment.id}
                          direction="row"
                          spacing={1.25}
                          sx={{
                            alignItems: 'flex-start',
                            flexDirection:
                              !comment.author_user_id && !comment.author_mobile_user_id
                                ? 'row'
                                : comment.is_internal
                                  ? 'row'
                                  : 'row-reverse',
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              flex: '0 0 auto',
                              fontSize: 11,
                              fontWeight: 700,
                              background:
                                !comment.author_user_id && !comment.author_mobile_user_id
                                  ? '#EEF2FF'
                                  : comment.is_internal
                                    ? '#EEF4FF'
                                    : '#F2F4F7',
                              color:
                                !comment.author_user_id && !comment.author_mobile_user_id
                                  ? '#4F46E5'
                                  : comment.is_internal
                                    ? '#2E73B8'
                                    : '#344054',
                            }}
                          >
                            {!comment.author_user_id && !comment.author_mobile_user_id
                              ? 'СИ'
                              : (selectedApartmentData?.owner_name || 'СЖ')
                                  .split(' ')
                                  .map((part) => part[0])
                                  .join('')
                                  .slice(0, 2)}
                          </Box>
                          <Box sx={{ maxWidth: 520, width: '100%' }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="baseline"
                              sx={{
                                mb: 0.5,
                                justifyContent:
                                  !comment.author_user_id && !comment.author_mobile_user_id
                                    ? 'flex-start'
                                    : comment.is_internal
                                      ? 'flex-start'
                                      : 'flex-end',
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#101828' }}>
                                {!comment.author_user_id && !comment.author_mobile_user_id
                                  ? 'Система'
                                  : comment.is_internal
                                    ? 'Сотрудник'
                                    : (selectedApartmentData?.owner_name || 'Жилец')}
                              </Typography>
                              <Typography sx={{ fontSize: 10.5, color: '#98A2B3', fontFamily: 'monospace' }}>
                                {formatDateTime(comment.created_at)}
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  !comment.author_user_id && !comment.author_mobile_user_id
                                    ? 'Системное событие'
                                    : comment.is_internal
                                      ? 'Внутренний'
                                      : 'Виден жильцу'
                                }
                                sx={{
                                  height: 20,
                                  borderRadius: 1,
                                  background:
                                    !comment.author_user_id && !comment.author_mobile_user_id
                                      ? '#EEF2FF'
                                      : comment.is_internal
                                        ? '#FFFAEB'
                                        : '#ECFDF3',
                                  color:
                                    !comment.author_user_id && !comment.author_mobile_user_id
                                      ? '#4F46E5'
                                      : comment.is_internal
                                        ? '#B54708'
                                        : '#027A48',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.8, fontSize: 10.5 },
                                }}
                              />
                            </Stack>
                            <Box
                              sx={{
                                borderRadius: 2,
                                px: 1.5,
                                py: 1.15,
                                border: '1px solid',
                                borderColor:
                                  !comment.author_user_id && !comment.author_mobile_user_id
                                    ? 'rgba(79, 70, 229, 0.22)'
                                    : comment.is_internal
                                      ? 'rgba(254, 200, 75, 0.45)'
                                      : '#E4E7EC',
                                background:
                                  !comment.author_user_id && !comment.author_mobile_user_id
                                    ? '#F5F3FF'
                                    : comment.is_internal
                                      ? '#FFFAEB'
                                      : '#F9FAFB',
                              }}
                            >
                              <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: '#344054' }}>
                                {comment.body}
                              </Typography>
                            </Box>
                          </Box>
                        </Stack>
                      )) : (
                        <Typography sx={{ fontSize: 12, color: '#98A2B3', py: 4, textAlign: 'center' }}>
                          Комментариев пока нет.
                        </Typography>
                      )}

                        </Stack>
                      </Box>

                      <Box sx={{ pt: 0.5, borderTop: '1px solid #F2F4F7' }}>
                        <Stack direction="row" spacing={0} sx={{ mb: 1.25, width: 'fit-content', border: '1px solid #D0D5DD', borderRadius: 1, overflow: 'hidden' }}>
                          <Button
                            variant="text"
                            disabled={!canEditTickets}
                            onClick={() => setCommentInternal(false)}
                            sx={{
                              minHeight: 32,
                              px: 1.5,
                              borderRadius: 0,
                              textTransform: 'none',
                              fontSize: 11.5,
                              color: commentInternal ? '#667085' : '#027A48',
                              background: commentInternal ? '#fff' : '#ECFDF3',
                            }}
                          >
                            Видно жильцу
                          </Button>
                          <Button
                            variant="text"
                            disabled={!canEditTickets}
                            onClick={() => setCommentInternal(true)}
                            sx={{
                              minHeight: 32,
                              px: 1.5,
                              borderRadius: 0,
                              textTransform: 'none',
                              fontSize: 11.5,
                              color: commentInternal ? '#B54708' : '#667085',
                              background: commentInternal ? '#FFFAEB' : '#fff',
                            }}
                          >
                            Внутренний
                          </Button>
                        </Stack>

                        <TextField
                          multiline
                          fullWidth
                          disabled={!canEditTickets}
                          minRows={3}
                          maxRows={6}
                          placeholder={commentInternal ? 'Напишите внутренний комментарий...' : 'Напишите сообщение для жильца...'}
                          value={commentBody}
                          onChange={(event) => setCommentBody(event.target.value)}
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              background: commentInternal ? '#FFFDF5' : '#fff',
                            },
                          }}
                        />

                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                          <Button
                            component="label"
                            variant="text"
                            startIcon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
                            disabled={!canEditTickets || !selectedTicket?.id || attachmentUploading}
                            sx={{ px: 0, minWidth: 'auto', textTransform: 'none', fontSize: 11.5, color: '#667085' }}
                          >
                            {attachmentUploading ? 'Загрузка...' : 'Прикрепить файл'}
                            <input type="file" hidden onChange={handleAttachmentFileSelect} />
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleAddComment}
                            disabled={!canEditTickets || commentSubmitting || !commentBody.trim()}
                            sx={{
                              minHeight: 32,
                              px: 1.75,
                              borderRadius: 1.25,
                              textTransform: 'none',
                              boxShadow: 'none',
                              fontSize: 12,
                              background: '#2E73B8',
                              '&:hover': { background: '#245B92' },
                            }}
                          >
                            {commentSubmitting ? 'Отправка...' : 'Отправить'}
                          </Button>
                        </Stack>

                        {attachmentUploading ? (
                          <LinearProgress
                            variant={attachmentUploadProgress > 0 ? 'determinate' : 'indeterminate'}
                            value={attachmentUploadProgress || 0}
                            sx={{ height: 5, borderRadius: 999, mt: 1 }}
                          />
                        ) : null}

                        {attachments.length ? (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {attachments.map((attachment) => (
                              <Button
                                key={attachment.id}
                                variant="outlined"
                                startIcon={<DownloadRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => handleDownloadAttachment(attachment.id)}
                                sx={{
                                  minHeight: 30,
                                  px: 1.2,
                                  borderRadius: 1.25,
                                  textTransform: 'none',
                                  color: '#344054',
                                  borderColor: '#D0D5DD',
                                  fontSize: 11,
                                }}
                              >
                                {attachment.file_name} · {formatFileSize(attachment.size_bytes)}
                              </Button>
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: { xs: '100%', md: 340 },
                    flex: { xs: '1 1 auto', md: '0 0 340px' },
                    flexShrink: 0,
                    background: '#F9FAFB',
                    borderTop: { xs: '1px solid #E4E7EC', md: 'none' },
                  }}
                >
                  <Box sx={{ background: '#fff', borderBottom: '1px solid #E4E7EC' }}>
                    <Box sx={{ px: 2, py: 1.15, borderBottom: '1px solid #F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' }}>
                        Лицевой счет и квартира
                      </Typography>
	                      <Button
	                        variant="text"
	                        disabled={!selectedApartmentData?.id}
	                        onClick={() => selectedApartmentData?.id && navigate(`/apartments/${selectedApartmentData.id}`)}
	                        sx={{ px: 0, minWidth: 'auto', textTransform: 'none', fontSize: 11, color: '#2E73B8' }}
	                      >
                        Профиль
                      </Button>
                    </Box>
                    <Box sx={{ px: 2, py: 1.25 }}>
                      {[
                        { label: 'Квартира', value: selectedApartmentData ? buildApartmentLabel(selectedApartmentData) : '—' },
                        { label: 'Адрес', value: selectedApartmentData?.address || '—' },
                        { label: 'Площадь', value: selectedApartmentData?.area ? `${selectedApartmentData.area} м²` : '—' },
                        { label: 'Лицевой счет', value: selectedApartmentData?.account_number || '—' },
                        { label: 'Дата создания', value: selectedApartmentData?.created_at ? formatDateOnly(selectedApartmentData.created_at) : '—' },
                        { label: 'Проживает', value: selectedApartmentData?.registered_people_count ? `${selectedApartmentData.registered_people_count} чел.` : '—' },
                        { label: 'Баланс', value: '—' },
                        { label: 'Долг', value: '—' },
                      ].map((row) => (
                        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.85, borderBottom: '1px solid #F2F4F7', '&:last-of-type': { borderBottom: 'none' } }}>
                          <Typography sx={{ fontSize: 11, color: '#98A2B3' }}>{row.label}</Typography>
                          <Typography sx={{ fontSize: 12, color: '#101828', fontWeight: 500, textAlign: 'right' }}>{row.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ background: '#fff', borderBottom: '1px solid #E4E7EC' }}>
                    <Box sx={{ px: 2, py: 1.15, borderBottom: '1px solid #F2F4F7' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' }}>
                        Приборы квартиры
                      </Typography>
                    </Box>
                    <Stack spacing={0} sx={{ px: 2, py: 0.5 }}>
                      {apartmentDevicesLoading ? (
                        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={22} /></Stack>
                      ) : apartmentDevices.length ? apartmentDevices.map((device) => (
                        <Box key={device.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1.2, borderBottom: '1px solid #F2F4F7', '&:last-of-type': { borderBottom: 'none' } }}>
                          <Box sx={{ width: 8, height: 8, mt: 0.7, borderRadius: '50%', background: device.status === 'ONLINE' ? '#12B76A' : device.status === 'OFFLINE' ? '#F04438' : '#F79009' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#101828' }}>
                              {device.system_type || device.name || 'Прибор'}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: '#98A2B3', fontFamily: 'monospace', mt: 0.1 }} noWrap>
                              {device.serial_number || 'Без серийного номера'}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 10.5, color: '#98A2B3', fontFamily: 'monospace' }}>
                              {formatDateOnly(device.last_seen_at || device.last_data_time)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#98A2B3' }}>проверка</Typography>
                          </Box>
                        </Box>
                      )) : (
                        <Typography sx={{ py: 2, fontSize: 12, color: '#98A2B3' }}>Приборы не найдены.</Typography>
                      )}
                    </Stack>
                  </Box>

                  <Box sx={{ background: '#fff' }}>
                    <Box sx={{ px: 2, py: 1.15, borderBottom: '1px solid #F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#344054' }}>
                        Последние показания
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: '#98A2B3', fontFamily: 'monospace' }}>
                        {formatDateOnly(new Date().toISOString())}
                      </Typography>
                    </Box>
                    <Stack spacing={0} sx={{ px: 2, py: 0.5 }}>
                      {apartmentDevicesLoading ? (
                        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={22} /></Stack>
                      ) : apartmentDeviceReadings.length ? apartmentDeviceReadings.map((device) => (
                        <Box key={`${device.id}-reading`} sx={{ py: 1.2, borderBottom: '1px solid #F2F4F7', '&:last-of-type': { borderBottom: 'none' } }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
                            <Typography sx={{ fontSize: 12, color: '#344054' }}>
                              {device.system_type || device.name || 'Прибор'}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#101828', fontFamily: 'monospace' }}>
                              {device.last_data || '—'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontSize: 10.5, color: '#98A2B3' }}>
                              {formatDateOnly(device.last_data_time || device.last_seen_at)}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: '#98A2B3', fontFamily: 'monospace' }}>
                              пред.: —
                            </Typography>
                          </Stack>
                        </Box>
                      )) : (
                        <Typography sx={{ py: 2, fontSize: 12, color: '#98A2B3' }}>Показаний пока нет.</Typography>
                      )}
                    </Stack>
                    <Box sx={{ px: 2, py: 1.15, borderTop: '1px solid #F2F4F7' }}>
                      <Button variant="text" sx={{ width: '100%', textTransform: 'none', fontSize: 11.5, color: '#2E73B8' }}>
                        Открыть историю
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 1.5, md: 3 },
        py: 2,
        maxWidth: 1460,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Stack spacing={1.5}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ ...surfaceSx, p: { xs: 1.5, md: 2 } }}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.55,
                justifyContent: 'flex-start',
                overflowX: 'auto',
                overflowY: 'hidden',
                pt: 0.35,
                pb: 0.25,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 6,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(152, 162, 179, 0.45)',
                  borderRadius: 999,
                },
              }}
            >
              {statusCards.map((card) => (
                <Paper
                  key={card.key}
                  onClick={() => setActiveStatusCard((current) => (current === card.key ? '' : card.key))}
                  sx={{
                    flex: '0 0 126px',
                    borderRadius: 2,
                    border: `1px solid ${card.tone.border}`,
                    background: card.tone.bg,
                    px: 1,
                    py: 0.65,
                    minHeight: 54,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease',
                    boxShadow: activeStatusCard === card.key ? `0 0 0 1px ${card.tone.color} inset` : 'none',
                    borderColor: activeStatusCard === card.key ? card.tone.color : card.tone.border,
                    transform: activeStatusCard === card.key ? 'translateY(-1px)' : 'none',
                    '&:hover': {
                      borderColor: card.tone.color,
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15, color: card.tone.color }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ mt: 0.1, fontSize: 10.5, lineHeight: 1.15, color: card.tone.color, opacity: 0.88 }}>
                    {card.value} ({total ? `${((card.value / total) * 100).toFixed(0)}%` : '0%'})
                  </Typography>
                </Paper>
              ))}
            </Box>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', lg: 'center' }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                {canEditTickets ? (
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineRoundedIcon />}
                    onClick={() => setCreateOpen(true)}
                    sx={{
                      minHeight: 42,
                      px: 2.1,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      boxShadow: 'none',
                      background: '#14A44D',
                      '&:hover': { background: '#0F8A40' },
                    }}
                  >
                    {createOpen ? 'Скрыть форму' : 'Создать заявку'}
                  </Button>
                ) : null}

                <TextField
                  size="small"
                  placeholder="Поиск заявки"
                  value={filters.search}
                  onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                  sx={{ minWidth: { xs: '100%', sm: 280 } }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <TextField
                  select
                  size="small"
                  value={filters.status}
                  onChange={(event) => {
                    setActiveStatusCard('');
                    setFilters((current) => ({ ...current, status: event.target.value }));
                  }}
                  SelectProps={{ MenuProps: selectMenuProps, displayEmpty: true }}
                  sx={{ ...dropdownFieldSx, minWidth: 180 }}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-status'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  value={filters.priority}
                  onChange={(event) => {
                    setActiveStatusCard('');
                    setFilters((current) => ({ ...current, priority: event.target.value }));
                  }}
                  SelectProps={{ MenuProps: selectMenuProps, displayEmpty: true }}
                  sx={{ ...dropdownFieldSx, minWidth: 180 }}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-priority'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={loadTickets}
                  disabled={loading}
                  sx={{ minHeight: 40, borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
                >
                  Обновить
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Dialog
          open={createOpen}
          onClose={() => {
            if (creating) return;
            resetCreateForm();
            setCreateOpen(false);
          }}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
              border: '1px solid var(--border-color)',
              boxShadow: '0 24px 64px rgba(16, 24, 40, 0.18)',
              background: 'var(--surface-bg)',
            },
          }}
        >
          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Новая заявка</Typography>
                <Typography variant="body2" color="text.secondary">
                  Создайте обращение и сразу привяжите его к нужной квартире.
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                <Autocomplete
                  size="small"
                  options={complexes}
                  value={selectedComplex}
                  slotProps={autocompleteSlotProps}
                  sx={dropdownFieldSx}
                  noOptionsText="Нет вариантов"
                  onChange={(_, nextValue) => handleCreateFormChange('complexId', nextValue ? String(nextValue.id) : '')}
                  getOptionLabel={(option) => option?.name || 'Без названия'}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  loading={complexesLoading}
                  renderInput={(params) => <TextField {...params} label="Комплекс" placeholder="Начните вводить название" />}
                />
                <Autocomplete
                  size="small"
                  options={buildingOptions}
                  value={selectedBuilding}
                  slotProps={autocompleteSlotProps}
                  sx={dropdownFieldSx}
                  noOptionsText="Нет вариантов"
                  onChange={async (_, nextValue) => {
                    const nextBuildingId = nextValue ? String(nextValue.id) : '';
                    handleCreateFormChange('buildingId', nextBuildingId);
                    if (nextBuildingId) {
                      await ensureCorpsLoaded(nextBuildingId);
                    }
                  }}
                  getOptionLabel={(option) => option?.name || option?.address || 'Здание'}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  disabled={!createForm.complexId}
                  renderInput={(params) => <TextField {...params} label="Здание" placeholder="Начните вводить адрес" />}
                />
                <Autocomplete
                  size="small"
                  options={corpsOptions}
                  value={selectedCorps}
                  slotProps={autocompleteSlotProps}
                  sx={dropdownFieldSx}
                  noOptionsText="Нет вариантов"
                  onChange={async (_, nextValue) => {
                    const nextCorpsId = nextValue ? String(nextValue.id) : '';
                    handleCreateFormChange('corpsId', nextCorpsId);
                    if (nextCorpsId) {
                      await loadApartmentsByCorpForCreate(nextCorpsId);
                    }
                  }}
                  getOptionLabel={(option) => option?.name || 'Корпус'}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  disabled={!createForm.buildingId}
                  renderInput={(params) => <TextField {...params} label="Корпус" placeholder="Начните вводить название корпуса" />}
                />
                <Autocomplete
                  size="small"
                  options={apartmentOptions}
                  value={selectedApartment}
                  slotProps={autocompleteSlotProps}
                  sx={dropdownFieldSx}
                  noOptionsText="Нет вариантов"
                  onChange={(_, nextValue) => handleCreateFormChange('apartmentId', nextValue ? String(nextValue.id) : '')}
                  getOptionLabel={(option) => buildApartmentLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  disabled={!createForm.corpsId}
                  renderInput={(params) => <TextField {...params} label="Квартира" placeholder="Начните вводить номер квартиры" />}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.35fr) repeat(3, minmax(0, 0.8fr))' }, gap: 1 }}>
                <TextField size="small" label="Тема заявки" value={createForm.title} onChange={(event) => handleCreateFormChange('title', event.target.value)} />
                <TextField select size="small" label="Категория" value={createForm.category} onChange={(event) => handleCreateFormChange('category', event.target.value)} sx={dropdownFieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Приоритет" value={createForm.priority} onChange={(event) => handleCreateFormChange('priority', event.target.value)} sx={dropdownFieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                  {PRIORITY_OPTIONS.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label="Источник" value={createForm.source} onChange={(event) => handleCreateFormChange('source', event.target.value)} sx={dropdownFieldSx} SelectProps={{ MenuProps: selectMenuProps }}>
                  {SOURCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
                <Autocomplete
                  size="small"
                  options={assignees}
                  value={selectedCreateAssignee}
                  slotProps={autocompleteSlotProps}
                  sx={dropdownFieldSx}
                  noOptionsText="Нет доступных исполнителей"
                  loading={assigneesLoading}
                  onChange={(_, nextValue) => handleCreateFormChange('assigneeUserId', nextValue ? String(nextValue.id) : '')}
                  getOptionLabel={(option) => option?.full_name ? `${option.full_name} — ${mapRoleIdToLabel(option.role)}` : option?.email || 'Исполнитель'}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  renderInput={(params) => <TextField {...params} label="Исполнитель" placeholder="Назначить сразу" />}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Срок исполнения"
                  value={createForm.dueAt}
                  onChange={(event) => handleCreateFormChange('dueAt', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <TextField
                multiline
                minRows={3}
                maxRows={6}
                label="Описание"
                value={createForm.description}
                onChange={(event) => handleCreateFormChange('description', event.target.value)}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="text"
                    onClick={() => {
                      resetCreateForm();
                      setCreateOpen(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCreateTicket}
                    disabled={creating}
                    sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
                  >
                    {creating ? 'Создание...' : 'Создать заявку'}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Dialog>

        <Paper sx={{ ...surfaceSx, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1100 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.05fr 1fr 1.45fr 1.15fr 0.95fr 1.1fr 1fr 0.9fr 0.9fr 0.9fr',
                  px: 2,
                  py: 1.35,
                  background: '#F8FAFC',
                  borderBottom: '1px solid var(--border-color)',
                }}
              >
                {['Заявка', 'Создана', 'Квартира / адрес', 'Заявитель', 'Тип', 'Исполнитель', 'Срок', 'Статус', 'Приоритет', 'Источник'].map((label) => (
                  <Typography key={label} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#667085' }}>
                    {label}
                  </Typography>
                ))}
              </Box>

              {loading ? (
                <Stack alignItems="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </Stack>
              ) : filteredTickets.length ? filteredTickets.map((ticket, index) => {
                const status = statusChipProps(ticket.status);
                const priority = priorityChipProps(ticket.priority);
                const apartment = apartmentMap[ticket.apartment_id];
                const assignee = assignees.find((item) => String(item.id) === String(ticket.assignee_user_id));
                const dueCaption = ticket.due_at ? formatDeadlineCaption(ticket.due_at) : 'Без срока';

                return (
                  <Box
                    key={ticket.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/tickets/${ticket.id}`);
                      }
                    }}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.05fr 1fr 1.45fr 1.15fr 0.95fr 1.1fr 1fr 0.9fr 0.9fr 0.9fr',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      background: index % 2 === 0 ? '#fff' : '#FCFDFD',
                      borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                      transition: 'background 160ms ease',
                      '&:hover': { background: 'rgba(46, 115, 184, 0.05)' },
                    }}
                  >
                    <Box sx={{ pr: 1 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#2E73B8', fontFamily: 'monospace' }}>
                        #{ticket.number}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 12.5, fontWeight: 600, color: '#101828', lineHeight: 1.35 }}>
                        {ticket.title}
                      </Typography>
                    </Box>
                    <Typography sx={{ pr: 1, fontSize: 12.5, color: '#475467', fontFamily: 'monospace' }}>
                      {formatDateTime(ticket.created_at)}
                    </Typography>
                    <Box
                      sx={{
                        pr: 1,
                        cursor: apartment?.id ? 'pointer' : 'default',
                        borderRadius: 1.5,
                        '&:hover': apartment?.id
                          ? { background: 'rgba(46, 115, 184, 0.07)' }
                          : undefined,
                      }}
                      onClick={(event) => {
                        if (!apartment?.id) return;
                        event.stopPropagation();
                        navigate(`/apartments/${apartment.id}`);
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#101828' }}>
                        {buildApartmentLabel(apartment)}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 12, color: '#98A2B3', lineHeight: 1.35 }}>
                        {apartment?.owner_name || 'Квартира не загружена'}
                      </Typography>
                    </Box>
                    <Typography sx={{ pr: 1, fontSize: 12.5, color: '#344054' }}>
                      {apartment?.owner_name || 'Не указан'}
                    </Typography>
                    <Typography sx={{ pr: 1, fontSize: 12.5, color: '#344054' }}>
                      {getCategoryLabel(ticket.category)}
                    </Typography>
                    <Typography sx={{ pr: 1, fontSize: 12.5, color: assignee ? '#344054' : '#B42318' }}>
                      {getAssigneeLabel(assignee)}
                    </Typography>
                    <Box sx={{ pr: 1 }}>
                      <Typography sx={{ fontSize: 12.5, color: '#344054', fontWeight: 600 }}>
                        {ticket.due_at ? formatDateOnly(ticket.due_at) : 'Не задан'}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 12, color: dueCaption.startsWith('Просрочено') ? '#B42318' : '#98A2B3' }}>
                        {dueCaption}
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1 }}>
                      <Chip size="small" label={status.label} sx={{ height: 24, borderRadius: 1.5, background: status.bg, color: status.color, fontWeight: 700 }} />
                    </Box>
                    <Box sx={{ pr: 1 }}>
                      <Chip size="small" label={priority.label} sx={{ height: 24, borderRadius: 1.5, background: priority.bg, color: priority.color, fontWeight: 700 }} />
                    </Box>
                    <Typography sx={{ pr: 1, fontSize: 12.5, color: '#344054' }}>
                      {getSourceLabel(ticket.source)}
                    </Typography>
                  </Box>
                );
              }) : (
                <Stack spacing={1} alignItems="center" sx={{ px: 3, py: 8 }}>
                  <TaskAltRoundedIcon color="disabled" />
                  <Typography sx={{ fontWeight: 700 }}>Заявок пока нет</Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Когда появятся обращения, они отобразятся здесь.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
